import { useEffect, useState, useRef } from "react";
import { Input, ActionIcon, Box, Button, Flex, Popover } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { SearchIcon } from "../../shared/svg/SearchIcon";
import { ClearIcon } from "../../shared/svg/ClearIcon";
import { useMediaQuery } from "@mantine/hooks";
import { GetCoursesParams } from "../../types/api";
import useCoursesStore from "../../store/useCoursesStore";
import { FilterIcon } from "../../shared/svg/FilterIcon";
import { FiltersPopoverContent } from "./FiltersPopover";
import { SortPopoverContent } from "./SortPopover";
import { SortIcon } from "../../shared/svg/SortIcon";
import { useLocation, useNavigate } from "react-router";
import {
  capitalizeFirstLetter,
  formatPlatform,
  getLanguageName,
} from "../../utils/helpers";
import { OnboardingTour } from "@gfazioli/mantine-onboarding-tour";

interface Props {
  setCredentials: React.Dispatch<React.SetStateAction<GetCoursesParams>>;
  handleSearch: () => void;
  credentials: GetCoursesParams;
}

export const SearchBar: React.FC<Props> = ({
  setCredentials,
  handleSearch,
  credentials,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [lastSearchedTab, setLastSearchedTab] = useState<string | null>(null);
  const mobile = useMediaQuery("(max-width: 820px)");
  const { t } = useTranslation();
  const {
    currentTab,
    fetchCourses,
    fetchLearningCourses,
    fetchRecommendedCourses,
    filters,
  } = useCoursesStore();
  const [filterOpened, setFilterOpened] = useState(false);
  const [sortOpened, setSortOpened] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const prevTab = useRef<string | null>(null);

  const [shouldSearchAfterUpdate, setShouldSearchAfterUpdate] = useState(false);

  useEffect(() => {
    if (shouldSearchAfterUpdate) {
      handleSearch();
      setShouldSearchAfterUpdate(false); // обнуляємо, щоб не викликалося повторно
    }
  }, [credentials, shouldSearchAfterUpdate]);

  useEffect(() => {
    setCredentials((prev) => ({
      ...prev,
      search: searchValue,
    }));

    if (searchValue) {
      setLastSearchedTab(currentTab);
    }
  }, [searchValue]);

  // useEffect(() => {
  //   if (credentials.search === "") {
  //     handleSearch();
  //   }
  // }, [credentials.search]);

  useEffect(() => {
    if (
      credentials.search &&
      lastSearchedTab &&
      lastSearchedTab !== currentTab
    ) {
      setSearchValue("");
      setCredentials({});

      if (lastSearchedTab === "All Courses") fetchCourses({});
      if (lastSearchedTab === "Recommended") fetchRecommendedCourses({});
      if (lastSearchedTab === "My learning") fetchLearningCourses({});

      setLastSearchedTab(null);
    }

    prevTab.current = currentTab;
  }, [currentTab]);

  const handleClear = () => {
    setSearchValue("");
    handleSearch();
  };

  const formatLabel = (key: string) => {
    const map: Record<string, string> = {
      search: t("filter.search"),
      platform: t("filter.platform"),
      organization: t("filter.organization"),
      language: t("filter.language"),
      category: t("filter.category"),
      topic: t("filter.topic"),
      is_free: t("filter.is_free"),
      rating_min: t("filter.rating_min"),
      rating_max: t("filter.rating_max"),
      price_min: t("filter.price_min"),
      price_max: t("filter.price_max"),
      ordering: t("filter.ordering"),
      status: t("filters.status"),
    };

    return map[key] || key;
  };

  const formatOrderingValue = (value: string) => {
    const orderingMap: Record<string, string> = {
      "-rating": t("ordering.rating_desc"),
      rating: t("ordering.rating_asc"),
      "-price": t("ordering.price_desc"),
      price: t("ordering.price_asc"),
      "-is_free": t("sort.free_first"),
      is_free: t("sort.free_last"),
    };

    return orderingMap[value] || value;
  };

  const handleRemoveFilter = (key: string, valueToRemove?: string) => {
    setCredentials((prev) => {
      const updated = { ...prev };
      const currentValue = updated[key as keyof GetCoursesParams];

      const arrayKeys: (keyof GetCoursesParams)[] = ["platform", "language"];

      if (
        arrayKeys.includes(key as keyof GetCoursesParams) &&
        Array.isArray(currentValue) &&
        valueToRemove
      ) {
        const filtered = currentValue.filter((val) => val !== valueToRemove);

        if (filtered.length > 0) {
          updated[key as "platform" | "language"] = filtered; // ⬅️ Тут ми явно вказуємо ключ
        } else {
          delete updated[key as keyof GetCoursesParams];
        }
      } else {
        delete updated[key as keyof GetCoursesParams];
      }

      return updated;
    });

    const params = new URLSearchParams(location.search);
    const categoryParam = params.get("category");
    if (categoryParam) {
      navigate("/courses");
    }

    setShouldSearchAfterUpdate(true);
  };

  return (
    <Box mt={24} mb={8}>
      <Flex gap="sm" mb={8}>
        <OnboardingTour.Target id="courses-search">
          <Flex gap="sm" w="100%" mb={8}>
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              placeholder={t("search_placeholder")}
              radius="md"
              size={mobile ? "sm" : "md"}
              leftSection={<SearchIcon />}
              rightSection={
                searchValue && (
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={handleClear}
                    aria-label="Clear search"
                    style={{ pointerEvents: "all" }}
                  >
                    <ClearIcon />
                  </ActionIcon>
                )
              }
              styles={{
                input: {
                  paddingLeft: 38,
                  fontSize: 16,
                  height: mobile ? 36 : 42,
                  paddingRight: searchValue ? 38 : 14,
                },
                section: {
                  top: 0,
                  right: 8,
                  zIndex: 0,
                },
              }}
              maw={985}
              w="100%"
            />

            <Button
              variant="filled"
              color="violet"
              size={mobile ? "sm" : "md"}
              onClick={handleSearch}
              h={mobile ? 36 : 42}
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              {t("search_button")}
            </Button>
          </Flex>
        </OnboardingTour.Target>

        <Popover
          width={300}
          position={mobile ? "bottom" : "bottom-end"}
          withArrow
          shadow="md"
          opened={filterOpened}
          onChange={setFilterOpened}
          closeOnClickOutside={true}
          trapFocus={false}
        >
          <OnboardingTour.Target id="courses-filter">
            <div>
              <Popover.Target>
                <ActionIcon
                  variant="subtle"
                  color="dark-gray"
                  size={mobile ? "sm" : "md"}
                  onClick={() => setFilterOpened((prev) => !prev)}
                  h={mobile ? 36 : 42}
                  w={mobile ? 36 : 42}
                >
                  <FilterIcon />
                </ActionIcon>
              </Popover.Target>
            </div>
          </OnboardingTour.Target>

          <Popover.Dropdown
            onTouchStart={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <FiltersPopoverContent
              credentials={credentials}
              setCredentials={setCredentials}
              handleSearch={handleSearch}
              filterOptions={filters}
              onClose={() => setFilterOpened(false)}
            />
          </Popover.Dropdown>
        </Popover>

        <Popover
          width={250}
          position={mobile ? "bottom" : "bottom-end"}
          withArrow
          shadow="md"
          opened={sortOpened}
          onChange={setSortOpened}
          closeOnClickOutside={true}
          trapFocus={false}
        >
          <OnboardingTour.Target id="courses-sort">
            <div>
              <Popover.Target>
                <ActionIcon
                  variant="subtle"
                  color="dark-gray"
                  size={mobile ? "sm" : "md"}
                  onClick={() => setSortOpened((prev) => !prev)}
                  h={mobile ? 36 : 42}
                  w={mobile ? 36 : 42}
                  aria-label="Sort"
                >
                  <SortIcon />
                </ActionIcon>
              </Popover.Target>
            </div>
          </OnboardingTour.Target>

          <Popover.Dropdown
            onTouchStart={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <SortPopoverContent
              handleSearch={handleSearch}
              credentials={credentials}
              setCredentials={setCredentials}
              onClose={() => setSortOpened(false)}
            />
          </Popover.Dropdown>
        </Popover>
      </Flex>
      {Object.entries(credentials)
        .filter(
          ([key, value]) =>
            value !== undefined && value !== "" && key !== "search"
        )
        .flatMap(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((val) => ({
              key,
              value: val,
              display:
                key === "language"
                  ? `${formatLabel(key)}: ${getLanguageName(val)}`
                  : key === "platform"
                  ? `${formatLabel(key)}: ${formatPlatform(val)}`
                  : `${formatLabel(key)}: ${val}`,
            }));
          }

          return [
            {
              key,
              value,
              display:
                key === "is_free"
                  ? formatLabel(key)
                  : key === "ordering"
                  ? `${formatLabel(key)}: ${formatOrderingValue(
                      value as string
                    )}`
                  : key === "platform"
                  ? `${formatLabel(key)}: ${formatPlatform(value)}`
                  : key === "language"
                  ? `${formatLabel(key)}: ${getLanguageName(value)}`
                  : key === "topic"
                  ? `${formatLabel(key)}: ${capitalizeFirstLetter(value)}`
                  : key === "status"
                  ? `${formatLabel(key)}: ${t(value)}`
                  : `${formatLabel(key)}: ${value}`,
            },
          ];
        })
        .map(({ key, value, display }) => (
          <Box
            key={`${key}-${value}`}
            component="span"
            mr={8}
            mt={8}
            px={10}
            py={4}
            style={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: "#F3F4F6",
              borderRadius: 20,
              fontSize: 14,
            }}
          >
            <span style={{ marginRight: 6 }}>{display}</span>
            <ActionIcon
              size="xs"
              variant="transparent"
              color="gray"
              onClick={() => handleRemoveFilter(key, value)}
            >
              <ClearIcon />
            </ActionIcon>
          </Box>
        ))}
    </Box>
  );
};
